import axios from 'axios';
import { authService } from './authService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:42026';

/** One volume band of a tiered price (dollars per seat; upTo null = the open-ended top band). */
export type PriceTierBand = { upTo: number | null; unitAmount: number | null };

/**
 * One tier/plan slot from GET /api/stripe/config `tiers` (Sept 2026 pricing).
 * `amount` is the price at quantity 1 (annual slots carry the full-year
 * amount); `perSeatAmount` is the single-seat rate in dollars; `tiers` is the
 * volume band table, null on flat per_unit prices (all Pro prices are flat).
 */
export type StripeTierPrice = {
  priceId?: string;
  productId?: string;
  amount: number;
  currency?: string;
  formatted: string;
  interval?: string;
  perSeatAmount: number | null;
  tiers: PriceTierBand[] | null;
} | null;

export type StripeTiersConfig = {
  pro?: { annual?: StripeTierPrice; monthly?: StripeTierPrice };
  max?: { annual?: StripeTierPrice; monthly?: StripeTierPrice };
} | null;

/** The volume band table of a tier price, normalized so no row carries junk. */
function normalizeBandTable(raw: unknown): PriceTierBand[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const bands = raw
    .filter((band): band is Record<string, unknown> => !!band && typeof band === 'object')
    .map((band) => ({
      upTo: typeof band.upTo === 'number' && Number.isFinite(band.upTo) ? band.upTo : null,
      unitAmount: typeof band.unitAmount === 'number' && Number.isFinite(band.unitAmount) ? band.unitAmount : null,
    }));
  return bands.length > 0 ? bands : null;
}

/** One tier/plan price slot, carrying the Sept 2026 volume fields through. */
function normalizeTierPrice(raw: unknown): StripeTierPrice {
  if (!raw || typeof raw !== 'object') return null;
  const slot = raw as Record<string, unknown>;
  if (typeof slot.amount !== 'number' || !Number.isFinite(slot.amount)) return null;
  return {
    priceId: typeof slot.priceId === 'string' ? slot.priceId : undefined,
    productId: typeof slot.productId === 'string' ? slot.productId : undefined,
    amount: slot.amount,
    currency: typeof slot.currency === 'string' ? slot.currency : undefined,
    formatted: typeof slot.formatted === 'string' ? slot.formatted : `$${slot.amount}`,
    interval: typeof slot.interval === 'string' ? slot.interval : undefined,
    perSeatAmount: typeof slot.perSeatAmount === 'number' && Number.isFinite(slot.perSeatAmount)
      ? slot.perSeatAmount
      : null,
    tiers: normalizeBandTable(slot.tiers),
  };
}

/** The per-tier price matrix from GET /api/stripe/config, or null when absent. */
function normalizeTiersConfig(raw: unknown): StripeTiersConfig {
  if (!raw || typeof raw !== 'object') return null;
  const tiers = raw as { pro?: Record<string, unknown>; max?: Record<string, unknown> };
  return {
    pro: {
      monthly: normalizeTierPrice(tiers.pro?.monthly),
      annual: normalizeTierPrice(tiers.pro?.annual),
    },
    max: {
      monthly: normalizeTierPrice(tiers.max?.monthly),
      annual: normalizeTierPrice(tiers.max?.annual),
    },
  };
}

/** The standing Max upgrade offer from /api/stripe/config (EPIC-15). */
export type UpgradePromo = { code: string; percentOff: number; durationMonths: number } | null;

function normalizeUpgradePromo(raw: unknown): UpgradePromo {
  if (!raw || typeof raw !== 'object') return null;
  const promo = raw as Record<string, unknown>;
  if (typeof promo.code !== 'string' || !promo.code.trim()) return null;
  return {
    code: promo.code.trim(),
    percentOff: typeof promo.percentOff === 'number' && Number.isFinite(promo.percentOff) ? promo.percentOff : 0,
    durationMonths: typeof promo.durationMonths === 'number' && Number.isFinite(promo.durationMonths) ? promo.durationMonths : 0,
  };
}

/** True if the message looks like a technical/axios error we should not show to the user. */
function isTechnicalErrorMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('status code') ||
    lower.includes('request failed') ||
    lower.includes('network error') ||
    lower.includes('econnrefused') ||
    lower.includes('econnreset') ||
    lower.includes('timeout') ||
    lower === 'error' ||
    /^\d{3}\s*$/.test(message.trim())
  );
}

class AccountService {
  private getAuthHeaders() {
    const token = authService.getToken();
    if (!token) {
      console.warn('No auth token available for API request');
    }
    return token ? { 'x-access-token': token } : {};
  }

  async getAccountStatus() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/user/account`, {
        headers: this.getAuthHeaders()
      });
      // Handle both { data: {...} } and direct object responses
      return response.data?.data || response.data || {};
    } catch (error: any) {
      console.error('Failed to get account status:', error);
      // On error, return pending_signup to block access (don't assume trial/active)
      return {
        status: 'pending_signup',
        trialStartDate: null,
        trialEndDate: null,
        trialPromptsLimit: null,
        trialPromptsUsed: 0,
        totalPrompts: 0,
        monthlyPrompts: 0
      };
    }
  }

  async getUsage(days?: number) {
    try {
      const url = days ? `${API_BASE_URL}/api/user/usage?days=${days}` : `${API_BASE_URL}/api/user/usage`;
      const response = await axios.get(url, {
        headers: this.getAuthHeaders()
      });
      return response.data || [];
    } catch (error) {
      console.error('Failed to get usage:', error);
      return [];
    }
  }

  /**
   * Per-provider drill-down statistics for the dashboard report modal:
   * { scope, userCount, activeUsers, totals, providers: [{ key, name, vendor,
   * totals, riskLevels, tools, daily }] }. Scoped server-side: Max tier spans
   * the whole company, Pro is the caller only.
   */
  async getUsageProviders(days: number = 30) {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/user/usage/providers?days=${days}`, {
        headers: this.getAuthHeaders()
      });
      return response.data || null;
    } catch (error) {
      console.error('Failed to get provider usage breakdown:', error);
      return null;
    }
  }

  async getSubscription() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/user/subscription`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get subscription:', error);
      throw error;
    }
  }

  async createSubscription(paymentMethodId: string, promoCode?: string, plan?: 'annual' | 'monthly', tier?: 'pro' | 'max') {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/user/subscription`,
        { paymentMethodId, promoCode: promoCode || undefined, plan: plan || undefined, tier: tier || undefined },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      const apiMessage = error.response?.data?.error;
      let message = typeof apiMessage === 'string' ? apiMessage : (error.message || 'Failed to create subscription');
      if (isTechnicalErrorMessage(message)) {
        message = 'Something went wrong. Please try again or try a different payment method.';
      }
      console.error('Failed to create subscription:', message, error.response?.status, error.response?.data);
      throw new Error(message);
    }
  }

  async validatePromoCode(promoCode: string, plan?: 'annual' | 'monthly') {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/stripe/validate-promo`,
        { promoCode: promoCode || '', plan: plan || undefined },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to validate promo code:', error);
      return {
        valid: false,
        error: error.response?.data?.error || 'Failed to validate promo code',
        baseAmount: 0,
        discountedAmount: 0,
        formatted: 'N/A'
      };
    }
  }

  async getStripeConfig() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/stripe/config`, {
        headers: this.getAuthHeaders()
      });
      console.log('Stripe config API response:', response.data);
      // API returns { publishableKey, plans: { annual, monthly, defaultPlan, annualSavingsPercent },
      // tiers: { pro: { monthly, annual }, max: { monthly, annual } }, defaultPlan, price, trialDays }
      const publishableKey = response.data?.publishableKey || '';
      const plans = response.data?.plans || null;
      // Sept 2026: tier slots carry perSeatAmount + tiers (the volume band
      // table) alongside the legacy amount/formatted fields; normalize so the
      // UI can rely on the shape.
      const tiers = normalizeTiersConfig(response.data?.tiers);
      const defaultPlan = response.data?.defaultPlan || plans?.defaultPlan || 'annual';
      const price = response.data?.price || null;
      // EPIC-15: trials are retired; 0 is the real value, never coerce it up.
      const trialDays = Number.isFinite(response.data?.trialDays) && response.data.trialDays >= 0
        ? response.data.trialDays
        : 0;
      const upgradePromo = normalizeUpgradePromo(response.data?.upgradePromo);
      if (!publishableKey) {
        console.error('No publishableKey in response:', response.data);
        return { publishableKey: '', plans: null, tiers: null, defaultPlan, price: null, trialDays, upgradePromo };
      }
      return { publishableKey, plans, tiers, defaultPlan, price, trialDays, upgradePromo };
    } catch (error: any) {
      console.error('Failed to get Stripe config:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      // Return empty config instead of throwing to show error message
      return { publishableKey: '', plans: null, tiers: null, defaultPlan: 'annual', price: null, trialDays: 0, upgradePromo: null };
    }
  }

  /**
   * Personal-account Pro/Max switch (EPIC-07 4.2). Payment failure surfaces
   * as a 402 with code PAYMENT_REQUIRED; the stored tier is unchanged then.
   * The raw axios error is rethrown so callers can inspect response codes.
   */
  async updateSubscriptionTier(tier: 'pro' | 'max') {
    const response = await axios.post(
      `${API_BASE_URL}/api/user/subscription/tier`,
      { tier },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async cancelSubscription() {
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/stripe/subscription`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      throw error;
    }
  }

  async getSubscriptionDetails() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/stripe/subscription/details`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get subscription details:', error);
      throw error;
    }
  }

  async updatePaymentMethod(paymentMethodId: string) {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/stripe/subscription/payment-method`,
        { paymentMethodId },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to update payment method:', error);
      throw error;
    }
  }

  async cancelCompanySubscription() {
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/stripe/subscription/company`, {
        headers: this.getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      console.error('Failed to cancel company subscription:', error);
      throw error;
    }
  }

  async createPaymentMethod(cardElement: any) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/stripe/payment-method`,
        { cardElement },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to create payment method:', error);
      throw error;
    }
  }

  async getCompany() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/company`, {
        headers: this.getAuthHeaders()
      });
      return response.data?.company || null;
    } catch (error: any) {
      console.error('Failed to get company:', error);
      return null;
    }
  }

  async getCompanyUsers() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/company/users`, {
        headers: this.getAuthHeaders()
      });
      return response.data?.users || [];
    } catch (error: any) {
      console.error('Failed to get company users:', error);
      throw error;
    }
  }

  async generateInviteLink() {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/company/invite`,
        {},
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to generate invite link:', error);
      throw error;
    }
  }

  async elevateUserToPro(userId: string) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/company/users/elevate`,
        { targetUserId: userId },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to elevate user to Pro:', error);
      throw error;
    }
  }

  /**
   * Admin Pro/Max seat move (EPIC-07 4.3): POST /api/company/users/set-tier.
   * Error contract: 409 { code: 'NO_SEATS_AVAILABLE' } on prepaid domains
   * with no free seat in the target tier, 402 { code: 'PAYMENT_REQUIRED' }
   * when the Stripe charge fails. The raw axios error is rethrown so callers
   * can branch on those codes.
   */
  async setUserTier(userId: string, tier: 'pro' | 'max') {
    const response = await axios.post(
      `${API_BASE_URL}/api/company/users/set-tier`,
      { targetUserId: userId, tier },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async makeUserAdmin(userId: string) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/company/users/make-admin`,
        { targetUserId: userId },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to make user admin:', error);
      throw error;
    }
  }

  async demoteUserToMember(userId: string) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/company/users/demote-member`,
        { targetUserId: userId },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to demote user to member:', error);
      throw error;
    }
  }

  async getUserAccountForAdmin(targetUserId: string) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/company/users/${targetUserId}/account`,
        { headers: this.getAuthHeaders() }
      );
      return response.data?.user || response.data;
    } catch (error: any) {
      console.error('Failed to get user account for admin:', error);
      throw error;
    }
  }

  async getUserUsageForAdmin(targetUserId: string, days: number = 30) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/company/users/${targetUserId}/usage?days=${days}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data || [];
    } catch (error: any) {
      console.error('Failed to get user usage for admin:', error);
      throw error;
    }
  }

  async getUserAuditLogsForAdmin(targetUserId: string, days: number = 30, limit: number = 100) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/company/users/${targetUserId}/audit-logs?days=${days}&limit=${limit}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data?.logs || response.data || [];
    } catch (error: any) {
      console.error('Failed to get user audit logs for admin:', error);
      throw error;
    }
  }
}

export const accountService = new AccountService();

